import { useCallback } from 'react';
import { ITicker } from '@kattana-io/common';
import Text from '../../atoms/Text';
import Clock from './Clock';
import { useNewPairsTableData } from './useNewPairsTableData';
import useTicker from '../../hooks/useTicker';
import {
  formatBaseVolume,
  formatUSD,
  getPrice,
} from '../../components/Modal/TickersModal/helpers';
import TableContractField from '../../components/TableComponents/Contract';
import TickerCell from '../../components/DexTrades/TickerCell';
import NumberDisplay from '../../molecules/NumberDisplay';
import DesktopTable, { ITableSort } from '../../molecules/Table/DesktopTable';
import { IRates } from '../../reducers/rates';
import { getPriceChange } from '../../helpers/priceChange';

export const LS_NEW_PAIRS_KEY = 'kattana-simple.new_pairs_table_columns';

interface INewPairsTableProps {
  pairs: ITicker[];
  rates: IRates;
  tableSort: ITableSort;
  width: number;
  loading: boolean;
  handleSort(id: string): void;
}

const NewPairsTable = ({
  pairs,
  rates,
  tableSort,
  width,
  loading,
  handleSort,
}: INewPairsTableProps) => {
  const { columns, data, tableColumnsWidth } = useNewPairsTableData({ pairs });

  const renderCell = (cell: any) => {
    const { chain, address } = cell.row.original;
    const key = `${chain}-${address}`;
    const rawTicker = cell.row.original;
    const ticker = useTicker(rawTicker);
    const price = getPrice(rawTicker, rates);
    const { priceChangePercent } = getPriceChange(price.rawPrice, rawTicker);

    switch (cell.column.id) {
    case 'pair':
      return <TickerCell path={key} ticker={cell.row.original} />;
    case 'pool_created':
      if (cell.value) {
        return <Clock timestamp={cell.value * 1000} />;
      }
      return null;
    case 'priceUSD':
      return (
        <div className='table_price_change'>
          <NumberDisplay
            value={price.rawPrice}
            startSymbol={price.sign}
            textProps={{ size: 12 }}
          />
          {!!priceChangePercent && (
            <NumberDisplay
              value={priceChangePercent}
              round={2}
              startSymbol={priceChangePercent > 0 ? '+' : ''}
              endSymbol='%'
              textProps={{ size: 12 }}
            />
          )}
        </div>
      );
    case 'liquidity':
      return <Text size={12}>{formatUSD(cell.value)}</Text>;
    case 'volume':
      return <Text size={12}>{formatBaseVolume(cell.value)}</Text>;
    case 'base_reserve':
      return (
        <NumberDisplay
          value={ticker.base_reserve}
          round={0}
          endSymbol={` ${ticker.base}`}
          textProps={{ size: 12 }}
        />
      );
    case 'quote_reserve':
      return (
        <NumberDisplay
          value={ticker.quote_reserve}
          round={0}
          endSymbol={` ${ticker.quote}`}
          textProps={{ size: 12 }}
        />
      );
    case 'contract':
      return <TableContractField address={cell.value} chain={chain} />;
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  /**
   * Change text color depending on price change
   */
  const getClassNameForRow = useCallback(
    (row: any) => {
      const price = getPrice(row, rates);
      const { priceChangePercent } = getPriceChange(price.rawPrice, row);

      if (priceChangePercent > 0) {
        return 'text--green-600';
      }
      if (priceChangePercent < 0) {
        return 'text--red-600';
      }
      return 'text--grey-400';
    },
    [rates]
  );

  return (
    <DesktopTable
      dataKey={LS_NEW_PAIRS_KEY}
      title='new_pairs'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      loading={loading}
      hiddenColumns={[]}
      rememberColWidth={false}
      withTableControls={false}
      withBorder={false}
      sort={tableSort}
      rowHeight={40}
      renderCell={renderCell}
      onSort={handleSort}
      getClassNameForRow={getClassNameForRow}
    />
  );
};

export default NewPairsTable;
